/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  MutableRefObject,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {areEqual} from 'react-window';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import {makeStyles} from '@material-ui/core/styles';
import {ClassNameMap} from '@material-ui/styles/withStyles';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {GlobalSearchResult} from '@ion/core/src/services/GlobalSearch';
import {Account, GeoFenceStatus, LatLong, User} from '@ion/core/src/types/entities';
import {HistoryInputEvents, HistoryReportTypeEnum, MapLayerSettings} from '@ion/dls/src/types/types';
import {PrimaryButton} from '../../components/buttons/PrimaryButton';
import Divider from '../../components/divider/Divider';
import {useMedia} from '../../hooks/useMedia';
import Hidden from '../../layout/Hidden';
import {dlsAlt, dlsColor, dlsTypography} from '../../theme/theme';
import {HeaderTypography} from '../../typography/HeaderTypography';
import {LIVE_HISTORY_LAYER_ID as LIVE_HISTORY_LAYER} from '../constants';
import CurrentLayerSettings, {CURRENT_LAYER} from './CurrentLayerSettings';
import {CUSTOM_LAYER} from './CustomLayerSettings';
import GeofenceLayerSettings, {GEOFENCE_LAYER} from './GeofenceLayerSettings';
import OperationalLayersSettings from './OperationalLayersSettings';
import SCDeviceLayerSettings, {SC_DEVICE_LAYER} from './SCDeviceLayerSettings';
import {THIRD_PARTY_LAYER} from './ThirdpartyLayerSettings';
import TrafficLayerContainer from './TrafficLayerContainer';
import {TRAFFIC_LAYER} from './TrafficLayerSettings';
import TrafficLayerTracking2Container from './TrafficLayerTracking2Container';
import TripHistoryLayerSettings, {HISTORY_LAYER} from './TripHistoryLayerSettings';
import WeatherLayerContainer from './WeatherLayerContainer';
import {WEATHER_LAYER} from './WeatherLayerSettings';
import WeatherLayerTracking2Container from './WeatherLayerTracking2Container';

// defines the prop definitions for the LayerChooser
export type LayerChooserProps = {
  map: __esri.Map;
  mapView: __esri.MapView;
  onClose?: any;
  findLabel?: any;
  onLayersChange?: Function;
  setTrafficSubLayers?: Function;
  selectedLayers: string[];
  trafficLayerURL?: string;
  weatherLayerURL?: string;
  onTrafficOpacityChange?: Function;
  onWeatherOpacityChange?: Function;
  onGeofenceOpacityChange?: Function;
  onGeofenceLabelChange?: Function;
  onGeofenceBorderOnlyChange?: Function;
  setSCDeviceAutoInitialized?: Function;
  geofenceBorderOnly?: boolean;
  isSCDeviceAutoInitialized?: boolean;
  geofenceLabelState?: boolean;
  onToggleClustering?: Function;
  onToggleLabelling?: Function;
  trafficOpacity?: number;
  weatherOpacity?: number;
  geofenceOpacity?: number;
  trafficSubLayers?: string[];
  editGeofenceGraphic?: __esri.Graphic;
  setEditGeofenceGraphic?: Function;
  isClusteringActive?: boolean;
  isLabellingActive?: boolean;
  isScDevicesLabelActive?: boolean;
  onToggleDeviceLabelling?: Function;
  onChangeDevicesLabelContent?: Function;
  show: boolean;
  savedGeofence?: object;
  geofenceStatus?: GeoFenceStatus;
  searchResult?: GlobalSearchResult | null;
  canEditGeofence?: boolean;
  selectedGeoFences?: Array<any>;
  user?: User;
  accounts?: Array<Account>;
  operationalLayers?: Array<Object>;
  onLayersOpacityChange?: Function;
  onLayerVisibilityChange?: Function;
  onSublayerVisibilityChange?: Function;
  islayerVisibilityUpdate?: boolean;
  assetsHistoryLoading?: boolean;
  loadAssetHistory?: Function;
  setAssetsHistoryLive?: Function;
  setHistoryInterval?: Function;
  setHistoryInputEvents?: Function;
  historyIntervalInHours?: number;
  isLargeFleetMode?: boolean;
  showHistoryRefresh?: boolean;
  historyReportType?: HistoryReportTypeEnum;
  historyInputEvents?: HistoryInputEvents[];
  resetHistoryReport?: Function;
  setHistoryReportType?: Function;
  setFindNearbyPopupForGeofence?: Function;
  mapLayerSettings: MapLayerSettings[];
  isRootAccount?: boolean;
  ancestorAccountIds?: number[];
  descendantAccountId?: number;
  externalClasses?: ClassNameMap<'root' | 'list'> ;
  isOpeningFromBottomSheet?: boolean;
  onRefreshAssets: Function;
  currentLocationRef?: MutableRefObject<LatLong>;
  isFindNearbyPopup?: boolean,
  onFindNearyBy?: Function,
  onFindNearbyPopupStatusChange?: Function,
  onFindNearbyBreadcrumbBackClick?: Function,
  onFindButtonStateChange?: Function,
  hasSCITrackingAccess?: boolean,
  labelContent?: string,
  scDevicesLabelContent?: string,
  onChangeLabelContent?: Function,
  moveAllToolsToTop?: boolean,
  isFMTracking?: boolean,
  MapControlsBreadCrumbs?: null | ReactElement;
  setIsLayersEnabled?: Function;
};

type StyleProps = {
  moveAllToolsToTop?: boolean;
  isDesktop?: boolean;
  MapControlsBreadCrumbs?: null | ReactElement;
};

const toggleStyle = (style1: string, style2: string) =>
  ({moveAllToolsToTop}: StyleProps) => moveAllToolsToTop ? style1 : style2;

// defines the styles used by the LayerChooser
const useStyles = makeStyles(theme => {
  const {
    fontFamily,
    fontSize,
    lineHeight
  } = dlsTypography('caption');
  return {
    list : {
      borderRadius                   : '4px',
      backgroundColor                : dlsColor('white'),
      boxShadow                      : dlsAlt('components', 'boxShadow', 'shadow4'),
      position                       : 'absolute',
      overflowX                      : 'hidden',
      overflowY                      : 'auto',
      top                            : 109,
      paddingTop                     : 0,
      maxWidth                       : ({isDesktop}: StyleProps) => isDesktop ? 320 : '100%',
      maxHeight                      : '96vh',
      right                          : toggleStyle('136px', '50px'),
      [theme.breakpoints.down('sm')] : {
        minWidth : '30vh',
        right    : '45px'
      },
      [theme.breakpoints.up('sm')] : {
        right     : '55px',
        maxHeight : 'calc(100vh - 300px)'
      }
    },
    listItem : {
      margin  : '0px 0px 0px 10px',
      padding : '0px 10px 0px 0px',
      width   : ({isDesktop}: StyleProps) => isDesktop ? 320 : '100%'
    },
    listItemHeaderContainer : {
      width        : '100%',
      height       : 26,
      background   : dlsColor('grey10'),
      borderRadius : '4px 4px 0px 0px'
    },
    listItemHeader : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'space-between',
      height         : '26px',
      paddingLeft    : '12px',
      background     : dlsColor('grey10'),
      borderRadius   : '4px 4px 0px 0px',
      textTransform  : 'uppercase',
      left           : '5px',
      top            : '5px',
      fontFamily     : 'Roboto !important',
      fontStyle      : 'normal',
      fontWeight     : 700,
      fontSize       : '12px !important',
      lineHeight     : '16px !important',
      letterSpacing  : '0.5px !important',
      color          : dlsColor('white')
    }
  };
});

// defines the header used by the LayerChooser
const ListHeader = ({
  isDesktop,
  MapControlsBreadCrumbs
}: StyleProps) => {
  const classes = useStyles({isDesktop});
  return (
    <div className={classes.listItemHeaderContainer}>
      {!isDesktop && MapControlsBreadCrumbs ? (
        <MapControlsBreadCrumbs crumb={translate('maps:layers.header')} isPlainCrumb />
      ) : (
        <HeaderTypography variant='h4' className={classes.listItemHeader}>
          {translate('maps:layers.header')}
        </HeaderTypography>
      )}
    </div>
  );
};

const LAYER_MAP = {
  [TRAFFIC_LAYER]      : TRAFFIC_LAYER,
  [WEATHER_LAYER]      : WEATHER_LAYER,
  [CURRENT_LAYER]      : CURRENT_LAYER,
  [GEOFENCE_LAYER]     : GEOFENCE_LAYER,
  [THIRD_PARTY_LAYER]  : THIRD_PARTY_LAYER,
  [CUSTOM_LAYER]       : CUSTOM_LAYER,
  [HISTORY_LAYER]      : HISTORY_LAYER,
  [LIVE_HISTORY_LAYER] : LIVE_HISTORY_LAYER,
  [SC_DEVICE_LAYER]    : SC_DEVICE_LAYER
};

const TRACKING2_LAYER_MAP_EXCLUDED = {
  [HISTORY_LAYER]   : HISTORY_LAYER,
  [SC_DEVICE_LAYER] : SC_DEVICE_LAYER
};

/**
 * Provides a utility which iterates over all enabled layers and returns
 * a mapping of enabled layer names.
 */
const getEnabledLayerNames = (layers: string[], isTracking2: boolean = false) => {
  const enabled = {};
  layers?.forEach(name => {
    if (!isTracking2 || (isTracking2 && !TRACKING2_LAYER_MAP_EXCLUDED[name])) {
      enabled[name] = true;
    }
  });
  return enabled;
};

const isLayerEnabled = (layers: string[], layerId: string): boolean => {
  const enabledLayers = getEnabledLayerNames(layers);
  return Boolean(enabledLayers[LAYER_MAP[layerId]]);
};

export const isCurrentEnabled = (layers: string[]): boolean => {
  return isLayerEnabled(layers, CURRENT_LAYER);
};

export const isHistoryEnabled = (layers: string[]): boolean => {
  return isLayerEnabled(layers, HISTORY_LAYER);
};

export const isLiveHistoryEnabled = (layers: string[]): boolean => {
  return isLayerEnabled(layers, LIVE_HISTORY_LAYER);
};

export const isTrafficEnabled = (layers: string[]): boolean => {
  return isLayerEnabled(layers, TRAFFIC_LAYER);
};

export const isWeatherEnabled = (layers: string[]): boolean => {
  return isLayerEnabled(layers, WEATHER_LAYER);
};

export const isGeoFenceEnabled = (layers: string[]): boolean => {
  return isLayerEnabled(layers, GEOFENCE_LAYER);
};

export const isThirdPartyEnabled = (layers: string[]): boolean => {
  return isLayerEnabled(layers, THIRD_PARTY_LAYER);
};

export const isCustomEnabled = (layers: string[]): boolean => {
  return isLayerEnabled(layers, CUSTOM_LAYER);
};

export const isSCDeviceEnabled = (layers: string[]): boolean => {
  return isLayerEnabled(layers, SC_DEVICE_LAYER);
};

export const isLayerVisible = (mapLayerSettings: MapLayerSettings[], settingKey: MapLayerSettings): boolean => {
  return mapLayerSettings?.includes(settingKey);
};

export const isCurrentLayerSettingsVisible = (mapLayerSettings: MapLayerSettings[]): boolean => {
  return isLayerVisible(mapLayerSettings, MapLayerSettings.CurrentLayerSettings);
};

export const isTripHistoryLayerSettingsVisible = (mapLayerSettings: MapLayerSettings[]): boolean => {
  return isLayerVisible(mapLayerSettings, MapLayerSettings.TripHistoryLayerSettings);
};

export const isGeofenceLayerSettingsVisible = (mapLayerSettings: MapLayerSettings[]): boolean => {
  return isLayerVisible(mapLayerSettings, MapLayerSettings.GeofenceLayerSettings);
};

export const isTrafficLayerSettingsVisible = (mapLayerSettings: MapLayerSettings[]): boolean => {
  return isLayerVisible(mapLayerSettings, MapLayerSettings.TrafficLayerSettings);
};

export const isWeatherLayerSettingsVisible = (mapLayerSettings: MapLayerSettings[]): boolean => {
  return isLayerVisible(mapLayerSettings, MapLayerSettings.WeatherLayerSettings);
};

export const isOperationalLayersSettingsVisible = (mapLayerSettings: MapLayerSettings[]): boolean => {
  return isLayerVisible(mapLayerSettings, MapLayerSettings.OperationalLayersSettings);
};

export const isSCDeviceSettingsVisible = (mapLayerSettings: MapLayerSettings[]): boolean => {
  return isLayerVisible(mapLayerSettings, MapLayerSettings.SupplyChainDeviceLayerSettings);
};

/**
 * @LayerChooser
 *
 * The Layer Chooser is displayed when a user clicks on the Layers Tool Icon
 * within a DLSMap, allowing users to select specific layers and the attributes
 * of the layers to be displayed within a DLSMap.
 *
 * The LayerChooserProps component represents the "Layers" DLS feature:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1904%3A13962
 *
 * ```typescript
 *
 *   <LayerChooser map={}
 *     show={}
 *     onClose={}
 *     {...layerChooserProps} />
 *
 * ```
 */
export const LayerChooser = ({
  map,
  selectedLayers,
  onLayersChange,
  setTrafficSubLayers,
  trafficLayerURL,
  weatherLayerURL,
  onTrafficOpacityChange,
  onWeatherOpacityChange,
  onGeofenceOpacityChange,
  onGeofenceBorderOnlyChange,
  onToggleClustering,
  setSCDeviceAutoInitialized,
  onToggleLabelling,
  onToggleDeviceLabelling,
  onChangeDevicesLabelContent,
  labelContent,
  onChangeLabelContent,
  isClusteringActive,
  isLabellingActive,
  trafficOpacity,
  trafficSubLayers,
  weatherOpacity,
  geofenceOpacity,
  geofenceBorderOnly,
  isSCDeviceAutoInitialized,
  geofenceLabelState,
  onClose,
  show = false,
  operationalLayers,
  onLayersOpacityChange,
  onLayerVisibilityChange,
  onSublayerVisibilityChange,
  islayerVisibilityUpdate,
  loadAssetHistory,
  assetsHistoryLoading,
  setAssetsHistoryLive,
  setHistoryInterval,
  setHistoryReportType,
  setHistoryInputEvents,
  historyReportType,
  historyIntervalInHours,
  showHistoryRefresh,
  resetHistoryReport,
  isLargeFleetMode,
  historyInputEvents,
  mapLayerSettings,
  externalClasses,
  onGeofenceLabelChange,
  geofenceStatus,
  isScDevicesLabelActive,
  scDevicesLabelContent,
  hasSCITrackingAccess,
  MapControlsBreadCrumbs,
  isOpeningFromBottomSheet = false,
  moveAllToolsToTop = false,
  isFMTracking = false,
  setIsLayersEnabled
}: LayerChooserProps) => {
  const {isDesktop}                         = useMedia();
  const classes: any                        = useStyles({moveAllToolsToTop, isDesktop});
  const [expandedLayer, setExpandedLayer]   = useState(null);
  const [scrollPosition, setScrollPosition] = useState(null);
  const [layers, toggleLayers]              = useState(selectedLayers);
  const [switchToggle, setToggle]           = useState(false);
  const [currentLayer, setCurrentLayer]     = useState(false);
  const [historyLayer, setHistoryLayer]     = useState(false);
  const showHistoryRefreshRef               = useRef(showHistoryRefresh);
  const enabledLayers                       = getEnabledLayerNames(selectedLayers, isFMTracking);

  /**
   * getLayers
   *
   * Provides a utility to set the CVS and HVS layer visibility as below:
   *  When the HVS layer is toggled visible (enabled), the CVS layer is automatically hidden (disabled).
   *  When the HVS layer is toggled hidden (disabled), the CVS layer is automatically made visible (enabled).
   *  When the CVS layer is toggled visible (enabled), the HVS layer is automatically hidden (disabled).
   *  BUT, when the CVS layer is toggled hidden (disabled),
   *    the HVS layer is/should NOT automatically be made visible (enabled).
   *
   * @param layer the layer name
   * @param toggle is the status true/false
   */
  const getLayers = useCallback((layer, toggle) => {
    let updates = !toggle ? selectedLayers.filter(e => e !== layer) : [...selectedLayers, layer];
    if (layer === CURRENT_LAYER && toggle) {
      setHistoryLayer(false);
      updates = updates.filter(i => i !== HISTORY_LAYER);
      if (showHistoryRefreshRef.current) {
        resetHistoryReport();
      }
    }
    if (layer === HISTORY_LAYER) {
      setHistoryLayer(toggle);
      if (toggle) {
        updates = updates.filter(i => i !== CURRENT_LAYER);
      } else {
        updates.push(CURRENT_LAYER);
      }
    }
    return updates;
  }, [selectedLayers]);

  const isSCDevicesEnabled = useMemo(() =>
    !isSCDeviceAutoInitialized || enabledLayers[LAYER_MAP[SC_DEVICE_LAYER]],
  [isSCDeviceAutoInitialized, enabledLayers]);

  const setLayers = useCallback((layer, toggle, label) => {
    const updates = getLayers(layer, toggle);
    const visibleOperationalLayers = (
      operationalLayers
        ?.filter(({title, visibility} = {}) => title !== layer && visibility)
        ?.map(({title} = {}) => title)
    ) || [];
    const visibleLayers = [...updates, ...visibleOperationalLayers];
    onLayersChange(visibleLayers);
    toggleLayers(visibleLayers);
    setCurrentLayer(label);
    setToggle(toggle);
  }, [operationalLayers, layers]);

  const onToggleExpand = useCallback((layer, isExpanded: boolean) => {
    setExpandedLayer(isExpanded ? layer : null);
  }, []);

  useEffect(() => {
    showHistoryRefreshRef.current = showHistoryRefresh;
  }, [showHistoryRefresh]);

  useEffect(() => {
    onToggleExpand(currentLayer, switchToggle);
  }, [switchToggle, currentLayer]);

  /**
   * Added a condition to check if History layer toggle gets failed because of any error,
   * then Asset Layer Toggle should be turned on(ION-2710)
   *
   */
  useEffect(() => {
    const match = selectedLayers?.find(layer => layer === 'history');
    if (historyLayer && match !== 'history' && !isCurrentEnabled(selectedLayers)) {
      setLayers(CURRENT_LAYER, true, true);
    } else {
      toggleLayers(selectedLayers);
    }
  }, [selectedLayers]);

  /**
   * Used to determine the icon to display in the LayerChooser.
   */
  useEffect(() => {
    setIsLayersEnabled?.(!isEmpty(enabledLayers));
  }, [enabledLayers]);

  /**
   * Preserve the Scroll position when clicking on sublayer [Desktop]
   */
  if (isDesktop) {
    const layerListContainer = document.getElementById('layerList');
    if (layerListContainer && !layerListContainer?.onscroll) {
      layerListContainer.onscroll = debounce(({target}) => {
        const {scrollTop} = target;
        layerListContainer.scrollTop = scrollTop;
        setScrollPosition(scrollTop);
      }, 250);
    };
    if (layerListContainer) {
      layerListContainer.scrollTop = scrollPosition;
    }
  }

  const TrafficLayerComponent = !isFMTracking ? TrafficLayerContainer : TrafficLayerTracking2Container;
  const WeatherLayerComponent = !isFMTracking ? WeatherLayerContainer : WeatherLayerTracking2Container;

  return (
    <div className={externalClasses?.root}
      style={{display: !show ? 'none' : 'contents'}}>
      <List id='layerList'
        className={classnames(classes.list, externalClasses?.list)}
        subheader={<ListHeader isDesktop={isDesktop} MapControlsBreadCrumbs={MapControlsBreadCrumbs} />}
        style={{display: isOpeningFromBottomSheet ? 'contents' : 'block'}}>
        {isCurrentLayerSettingsVisible(mapLayerSettings) && (
          <>
            <ListItem className={classes.listItem}>
              <CurrentLayerSettings onToggle={setLayers}
                onToggleExpand={onToggleExpand}
                onToggleClustering={onToggleClustering}
                onToggleLabelling={onToggleLabelling}
                labelContent={labelContent}
                onChangeLabelContent={onChangeLabelContent}
                expandedLayer={expandedLayer}
                isEnabled={enabledLayers[LAYER_MAP[CURRENT_LAYER]]}
                isLabellingActive={isLabellingActive}
                isClusteringActive={isClusteringActive}
                isLargeFleetMode={isLargeFleetMode}
                layers={layers}
                map={map} />
            </ListItem>
            <Divider />
          </>
        )}
        {isSCDeviceSettingsVisible(mapLayerSettings) && hasSCITrackingAccess && !isFMTracking && (
          <>
            <ListItem className={classes.listItem}>
              <SCDeviceLayerSettings onToggle={setLayers}
                onToggleExpand={onToggleExpand}
                expandedLayer={expandedLayer}
                setSCDeviceAutoInitialized={setSCDeviceAutoInitialized}
                isEnabled={isSCDevicesEnabled}
                onToggleLabelling={onToggleDeviceLabelling}
                isLabellingActive={isScDevicesLabelActive}
                labelContent={scDevicesLabelContent}
                onChangeLabelContent={onChangeDevicesLabelContent}
                layers={layers}
                map={map} />
            </ListItem>
            <Divider />
          </>
        )}
        {isTripHistoryLayerSettingsVisible(mapLayerSettings) && !isFMTracking && (
          <>
            <ListItem className={classes.listItem}>
              <TripHistoryLayerSettings onToggle={setLayers}
                onToggleExpand={onToggleExpand}
                expandedLayer={expandedLayer}
                isEnabled={enabledLayers[LAYER_MAP[HISTORY_LAYER]]}
                loadAssetHistory={loadAssetHistory}
                assetsHistoryLoading={assetsHistoryLoading}
                setAssetsHistoryLive={setAssetsHistoryLive}
                setHistoryInterval={setHistoryInterval}
                historyIntervalInHours={historyIntervalInHours}
                historyReportType={historyReportType}
                historyInputEvents={historyInputEvents}
                setHistoryReportType={setHistoryReportType}
                setHistoryInputEvents={setHistoryInputEvents}
                layers={layers}
                map={map} />
            </ListItem>
            <Divider />
          </>
        )}
        {isGeofenceLayerSettingsVisible(mapLayerSettings) && (
          <>
            <ListItem className={classes.listItem}>
              <GeofenceLayerSettings onToggle={setLayers}
                isEnabled={enabledLayers[LAYER_MAP[GEOFENCE_LAYER]]}
                selectedLayers={selectedLayers}
                onToggleExpand={onToggleExpand}
                onOpacityChange={onGeofenceOpacityChange}
                onBorderOnlyChange={onGeofenceBorderOnlyChange}
                onGeofenceLabelChange={onGeofenceLabelChange}
                borderOnly={geofenceBorderOnly}
                geofenceLabelState={geofenceLabelState}
                opacity={geofenceOpacity}
                expandedLayer={expandedLayer}
                geofenceStatus={geofenceStatus}
                layers={layers}
                map={map} />
            </ListItem>
            <Divider />
          </>
        )}
        {isTrafficLayerSettingsVisible(mapLayerSettings) && (
          <>
            <ListItem className={classes.listItem}>
              <TrafficLayerComponent isEnabled={enabledLayers[LAYER_MAP[TRAFFIC_LAYER]]}
                url={trafficLayerURL}
                onToggle={setLayers}
                onToggleExpand={onToggleExpand}
                onOpacityChange={onTrafficOpacityChange}
                opacity={trafficOpacity}
                setTrafficSubLayers={setTrafficSubLayers}
                trafficSubLayers={trafficSubLayers}
                layers={layers}
                map={map}
                expandedLayer={expandedLayer} />
            </ListItem>
            <Divider />
          </>
        )}
        {isWeatherLayerSettingsVisible(mapLayerSettings) && (
          <ListItem className={classes.listItem}>
            <WeatherLayerComponent isEnabled={enabledLayers[LAYER_MAP[WEATHER_LAYER]]}
              url={weatherLayerURL}
              onToggle={setLayers}
              onToggleExpand={onToggleExpand}
              onOpacityChange={onWeatherOpacityChange}
              opacity={weatherOpacity}
              layers={layers}
              map={map}
              expandedLayer={expandedLayer} />
          </ListItem>
        )}
        {isOperationalLayersSettingsVisible(mapLayerSettings) && (
          <ListItem className={classes.listItem}>
            <OperationalLayersSettings isEnabled
              map={null}
              operationalLayers={operationalLayers}
              onToggle={setLayers}
              onToggleExpand={onToggleExpand}
              onOpacityChange={onLayersOpacityChange}
              onLayerVisibilityChange={onLayerVisibilityChange}
              onSublayerVisibilityChange={onSublayerVisibilityChange}
              islayerVisibilityUpdate={islayerVisibilityUpdate}
              expandedLayer={expandedLayer}
              layers={layers} />
          </ListItem>
        )}
        {isOpeningFromBottomSheet && (
          <Hidden show='mobile'>
            <Divider />
            <div style={{marginTop: 25, width: '100%', position: 'fixed', bottom: '0px', right: '0px', backgroundColor: dlsColor('white')}}>
              <PrimaryButton fullWidth onMouseUp={onClose} padding='20px'>
                {translate('maps:controls.done')}
              </PrimaryButton>
            </div>
          </Hidden>
        )}
      </List>
    </div>
  );
};

export default memo(LayerChooser, areEqual);
